// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useCallback, useEffect, useRef, useState } from 'react';
import esriId from '@arcgis/core/identity/IdentityManager.js';
import { arcgisBaseUrl } from './mainconfig';

// How long the viewer waits for its ArcGIS token before it says the 3D view could not load (EXP-20261020-80). A
// host that drops the connection would otherwise hold the request until the browser gives up, about two minutes.
export const ARCGIS_TOKEN_TIMEOUT_MS = 25000;

export type ArcgisTokenStatus = 'pending' | 'registered' | 'failed';

const TOKEN_URL = `${arcgisBaseUrl}/DotNet_Test/proxy.ashx?getToken_AW`;
const TOKEN_SERVER = `${arcgisBaseUrl}/server/rest/services`;
const TOKEN_HOST = new URL(arcgisBaseUrl).host;

class TokenAnswerError extends Error {}

const requestToken = async (signal: AbortSignal) => {
  const response = await fetch(TOKEN_URL, { signal });
  if (!response.ok) throw new TokenAnswerError(`HTTP ${response.status}`);
  const { token, expires } = await response.json();
  if (!token) throw new TokenAnswerError('no token in the answer');
  return { token: String(token), expires: Number(expires) };
};

// Why the request failed, in words that never carry the answer itself (it may hold the token).
const failureReason = (err: unknown, timedOut: boolean) => {
  if (timedOut) return `no answer within ${ARCGIS_TOKEN_TIMEOUT_MS / 1000} s`;
  if (err instanceof TokenAnswerError) return err.message;
  if (err instanceof SyntaxError) return 'the answer is not JSON';
  return 'the connection failed';
};

// The viewer's ArcGIS token: requested on mount with a timeout, registered with the identity manager for the
// AirWorks ArcGIS server, and requested again by `retry`. Leaving the viewer ends a request still out.
export const useArcgisToken = () => {
  const [status, setStatus] = useState<ArcgisTokenStatus>('pending');
  const attemptRef = useRef<AbortController | null>(null);

  const register = useCallback(async () => {
    attemptRef.current?.abort();
    const attempt = new AbortController();
    attemptRef.current = attempt;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      attempt.abort();
    }, ARCGIS_TOKEN_TIMEOUT_MS);
    setStatus('pending');
    try {
      const { token, expires } = await requestToken(attempt.signal);
      if (attemptRef.current !== attempt) return;
      esriId.registerToken({ server: TOKEN_SERVER, token, expires });
      setStatus('registered');
    } catch (err) {
      if (attemptRef.current !== attempt) return;
      console.error(`ArcGIS token request to ${TOKEN_HOST} failed: ${failureReason(err, timedOut)}`);
      setStatus('failed');
    } finally {
      clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    register();
    return () => {
      const attempt = attemptRef.current;
      attemptRef.current = null;
      attempt?.abort();
    };
  }, [register]);

  return { status, retry: register };
};

export default useArcgisToken;
